// frontend/components/sidebar/TransferConfirmModal.tsx

"use client";

/**
 * 드래그앤드롭으로 개인↔팀 워크스페이스 간 문서를 이동하려 할 때
 * 표시되는 이동/복사 선택 확인 모달입니다.
 *
 * - PERSONAL → TEAM: "이동" 또는 "복사" 선택 가능
 * - TEAM → PERSONAL: "복사"만 가능 (팀 문서를 개인으로 이동은 불허)
 */

export interface TransferPayload {
  draggedDocId: string;
  targetParentId: string | null;
  targetWorkspaceType: string;
  draggedDoc: any;
}

interface TransferConfirmModalProps {
  isOpen: boolean;
  type: "TEAM_TO_PERSONAL" | "PERSONAL_TO_TEAM" | null;
  payload: TransferPayload | null;
  onClose: () => void;
  /** action: "MOVE"는 원본 삭제 후 이동, "COPY"는 원본 유지하며 복제 */
  onExecute: (action: "MOVE" | "COPY", payload: TransferPayload) => void;
}

export default function TransferConfirmModal({
  isOpen, type, payload, onClose, onExecute,
}: TransferConfirmModalProps) {
  if (!isOpen || !payload) return null;

  const isToPersonal = type === "TEAM_TO_PERSONAL";

  return (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-zinc-900 p-6 rounded-xl shadow-2xl w-400px border dark:border-zinc-800 animate-in zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-[15px] font-bold text-gray-900 dark:text-zinc-100 mb-2">
          {isToPersonal
            ? "이 페이지를 개인 페이지로 복사하시겠습니까?"
            : "이 페이지를 팀 스페이스로 옮기시겠습니까?"}
        </h3>
        <p className="text-sm text-gray-500 dark:text-zinc-400 mb-6 leading-relaxed">
          {isToPersonal
            ? "이 페이지는 본인만 접근할 수 있습니다."
            : "이 페이지는 허가 받은 인원은 모두 접근할 수 있습니다."}
        </p>

        <div className="flex flex-col space-y-2">
          {/* 개인→팀 이동 시에만 "이동하기" 버튼 표시 */}
          {!isToPersonal && (
            <button
              onClick={() => onExecute("MOVE", payload)}
              className="w-full px-4 py-2.5 text-[13px] font-bold bg-[#006DBA] text-white rounded-lg hover:bg-[#005A9E] transition-colors"
            >
              팀 스페이스로 이동하기
            </button>
          )}

          <button
            onClick={() => onExecute("COPY", payload)}
            className={`w-full px-4 py-2.5 text-[13px] font-bold rounded-lg transition-colors ${
              isToPersonal
                ? "bg-[#006DBA] text-white hover:bg-[#005A9E]"
                : "bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-zinc-300 hover:bg-gray-200 dark:hover:bg-zinc-700"
            }`}
          >
            {isToPersonal ? "개인 페이지로 복사하기" : "팀 스페이스로 복사하기"}
          </button>

          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 text-[13px] font-medium text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5 rounded-lg transition-colors mt-2"
          >
            취소
          </button>
        </div>
      </div>
    </div>
  );
}
